/**
 * voiceClonePollAdapter - Normalizes voice clone upload/training job status
 * into PollUpdate consumed by GenerationContext.
 *
 * Voice clone creation is driven by voiceApi.uploadVoice() which is a
 * synchronous XHR upload — the backend does not return a job_id; the voice
 * is ready as soon as the upload completes. There is therefore no async
 * polling phase in the current implementation.
 *
 * The adapter is registered here so that GenerationContext can handle any
 * persisted 'voiceclone' job records from localStorage (e.g. from a future
 * long-running training flow). For now it always returns a terminal 'failed'
 * update with a clear message so stale records drain cleanly.
 *
 * Self-registers on module load.
 */

import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface VoiceCloneJobResult {
  voiceId: string;
  voiceName: string;
  audioUrl?: string;
}

export const voiceClonePollAdapter: PollAdapter = async (
  _jobId: string
): Promise<PollUpdate<VoiceCloneJobResult>> => {
  // Voice clone upload is synchronous; no backend job to poll.
  // If a stale job_id appears in localStorage this adapter drains it cleanly.
  return {
    done: true,
    status: 'failed',
    error: {
      message: 'Voice clone job not found — voice creation is synchronous and requires no polling.',
      code: 'NO_ASYNC_JOB',
      sanitized: sanitizeBackendError({ code: 'NO_ASYNC_JOB' }),
    },
  };
};

registerPollAdapter('voiceclone', voiceClonePollAdapter);
